import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CompanionClient, StreamEvent, ConnectionState } from '@ac/client';
import * as z from 'zod';
import { RpcError } from '@ac/client';
import { MessageBubble } from './MessageBubble';
import { ConnectionIndicator } from './ConnectionIndicator';
import { TypingIndicator } from './TypingIndicator';
import { ErrorBanner } from './ErrorBanner';
import { ExportModal } from './ExportModal';
import { SettingsMenu } from './SettingsMenu';
import { ThemeToggle } from './ThemeToggle';
import { WelcomeMessage } from './WelcomeMessage';
import { Spinner } from './Spinner';
import { EmotionCheckIn } from './EmotionCheckIn';
import { EmotionEntryMessage } from './EmotionEntryMessage';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibilityAnnouncer } from '../utils/accessibility';
import { getApiBaseUrl } from '../config/api';
import type { Message, ErrorState } from '../types';
import { SmilePlus } from 'lucide-react';

export function CompanionChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [error, setError] = useState<ErrorState | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showEmotionCheckIn, setShowEmotionCheckIn] = useState(false);
  const [offlineMessageCount, setOfflineMessageCount] = useState(0);
  const [retryState, setRetryState] = useState<{messageId: string, attempts: number, maxAttempts: number} | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const clientRef = useRef<CompanionClient | null>(null);
  const streamingToolCalls = useRef<any[]>([]);

  const { token, isTokenReady, isLoading: authLoading } = useAuth();
  const announce = useAccessibilityAnnouncer();
  
  // Track client initialization state
  const [clientInitialized, setClientInitialized] = useState(false);

  /**
   * Determine if an error type should trigger automatic retry
   */
  const shouldAutoRetry = (errorType: string): boolean => {
    const retryableTypes = [
      'server_error',
      'bad_gateway', 
      'service_unavailable',
      'gateway_timeout',
      'timeout',
      'network_error'
    ];
    return retryableTypes.includes(errorType);
  };

  useEffect(() => {
    console.log('CompanionChat: useEffect triggered, token:', token ? 'Present' : 'Missing', 'tokenReady:', isTokenReady);
    
    if (!token || !isTokenReady) {
      console.log('CompanionChat: Token not ready, cleaning up client');
      // Comprehensive cleanup when no token or token not ready
      cleanupClient();
      return;
    }

    console.log('CompanionChat: Token ready, initializing/reinitializing client');
    initializeClient(token).catch(error => {
      console.error('CompanionChat: Failed to initialize client:', error);
      setError({
        type: 'initialization',
        message: 'Failed to initialize chat client',
        retryable: true
      });
    });

    // Listen for auth updates that don't trigger token dependency change
    const handleAuthUpdate = (event: CustomEvent) => {
      console.log('CompanionChat: Received auth update event:', event.detail);
      if (event.detail?.token && event.detail?.isAuthenticated && event.detail.token !== token) {
        // Token changed but useEffect hasn't fired yet
        reinitializeClient(event.detail.token);
      } else if (!event.detail?.token || !event.detail?.isAuthenticated) {
        // Token was cleared or auth failed
        cleanupClient();
      }
    };
    
    window.addEventListener('auth-token-updated', handleAuthUpdate as EventListener);
    window.addEventListener('auth-state-changed', handleAuthUpdate as EventListener);

    // Periodically check connection status
    const connectionCheckInterval = setInterval(() => {
      if (clientRef.current && token) {
        clientRef.current.checkConnection().then((state) => {
          console.log('CompanionChat: Periodic connection check:', state);
        }).catch((error) => {
          console.warn('CompanionChat: Connection check failed:', error);
        });
      }
    }, 10000); // Check every 10 seconds

    return () => {
      console.log('CompanionChat: Cleaning up auth event listeners and intervals');
      window.removeEventListener('auth-token-updated', handleAuthUpdate as EventListener);
      window.removeEventListener('auth-state-changed', handleAuthUpdate as EventListener);
      clearInterval(connectionCheckInterval);
    };
  }, [token, isTokenReady]);

  /**
   * Initialize new CompanionClient instance
   */
  const initializeClient = async (authToken: string) => {
    try {
      console.log('CompanionChat: Initializing new CompanionClient');
      
      // Clean up existing client first
      if (clientRef.current) {
        clientRef.current.cleanup();
      }
      
      setClientInitialized(false);
      
      clientRef.current = new CompanionClient({
        baseUrl: getApiBaseUrl(),
        token: authToken,
        onStreamEvent: handleStreamEvent,
        onError: handleError,
        onConnectionChange: setConnectionState,
        onOfflineMessage: handleOfflineMessage,
        onExportWarning: handleExportWarning,
        onAccountDeleted: handleAccountDeleted,
        onTabMessage: handleTabMessage,
        enableMultiTab: true
      });

      await loadCachedConversations();
      setClientInitialized(true);
      console.log('CompanionChat: Client initialized successfully');
    } catch (error) {
      console.error('CompanionChat: Failed to initialize client:', error);
      setClientInitialized(false);
      setError({
        type: 'initialization_error',
        title: 'Initialization Error',
        message: 'Failed to initialize chat client. Please refresh the page.',
        icon: '⚠️',
        retryable: true
      });
    }
  };

  /**
   * Reinitialize client with new token
   */
  const reinitializeClient = (newToken: string) => {
    try {
      console.log('CompanionChat: Reinitializing client with new token');
      
      setClientInitialized(false);
      
      if (clientRef.current) {
        clientRef.current.reinitialize(newToken);
      } else {
        initializeClient(newToken).catch(console.error);
        return; // initializeClient will set clientInitialized
      }
      
      setClientInitialized(true);
      // Clear any auth-related errors
      setError(null);
      console.log('CompanionChat: Client reinitialized successfully');
    } catch (error) {
      console.error('CompanionChat: Failed to reinitialize client:', error);
      setClientInitialized(false);
      // Fallback to full initialization
      initializeClient(newToken).catch(console.error);
    }
  };

  /**
   * Clean up client and reset state
   */
  const cleanupClient = () => {
    try {
      console.log('CompanionChat: Cleaning up client');
      
      if (clientRef.current) {
        clientRef.current.cleanup();
        clientRef.current = null;
      }
      
      // Reset component state
      setClientInitialized(false);
      setIsStreaming(false);
      setStreamingMessage('');
      setConnectionState('disconnected');
      setOfflineMessageCount(0);
      streamingToolCalls.current = [];
      
      console.log('CompanionChat: Client cleanup completed');
    } catch (error) {
      console.error('CompanionChat: Error during client cleanup:', error);
    }
  };

  const handleStreamEvent = useCallback((event: StreamEvent) => {
    switch (event.type) {
      case 'content':
        setStreamingMessage(prev => prev + event.data);
        break;

      case 'tool_call':
        streamingToolCalls.current.push(event.data);
        
        if (event.data.name === 'save_fact') {
          setStreamingMessage(prev =>
            prev + `\n\n*💾 Remembering: "${event.data.arguments.fact_text}"*\n\n`
          );
        } else if (event.data.name === 'search_conversations') {
          setStreamingMessage(prev =>
            prev + `\n\n*🔍 Searching: "${event.data.arguments.query}"*\n\n`
          );
        }
        break;

      case 'error':
        const rpcError = RpcError.safeParse(event.data);
        if (rpcError.success) {
          handleRpcError(rpcError.data);
        } else {
          setError({
            type: 'error',
            message: 'An unexpected error occurred'
          });
        }
        setIsStreaming(false);
        break;

      case 'done':
        setIsStreaming(false);
        announce('AI has finished responding');
        break;
    }
  }, []);

  const handleRpcError = (error: z.infer<typeof RpcError>) => {
    const errorMap: Record<string, ErrorState> = {
      'LLM_UNAVAILABLE': {
        type: 'llm_unavailable',
        title: 'AI Temporarily Unavailable',
        message: 'Our AI is experiencing high demand. We\'re using a backup model for now.',
        icon: '⚡',
        retryable: true
      },
      'MODERATION_BLOCKED': {
        type: 'moderation_blocked',
        title: 'Message Blocked',
        message: 'Your message was blocked by our content policy. Please try rephrasing.',
        icon: '🛡️',
        retryable: false,
        showPolicyLink: true
      },
      'RATE_LIMITED': {
        type: 'rate_limited',
        title: 'Slow Down',
        message: 'You\'re sending messages too quickly. Please wait a moment.',
        icon: '⏱️',
        retryable: true,
        retryAfter: 30
      }
    };

    const errorInfo = errorMap[error.code] || {
      type: 'error',
      title: 'Error',
      message: error.message,
      icon: '❌'
    };

    setError(errorInfo);
  };

  const handleError = useCallback((error: any) => {
    console.log('CompanionChat: Handling error:', {
      type: error.type,
      title: error.title,
      message: error.message,
      retryable: error.retryable
    });
    
    if (error.type === 'reconnecting') {
      setError({
        type: 'reconnecting',
        title: 'Reconnecting',
        message: error.resuming ? 
          `Reconnecting and resuming stream... (attempt ${error.attempt})` :
          `Reconnecting... (attempt ${error.attempt})`,
        icon: '🔄',
        temporary: true
      });
    } else if (error.type === 'auth_failed' || error.type === 'auth_timeout') {
      // Handle auth failures by cleaning up and marking client as not ready
      console.log('CompanionChat: Auth error detected, cleaning up client');
      setClientInitialized(false);
      cleanupClient();
      
      setError({
        type: error.type,
        title: error.title || 'Authentication Required',
        message: error.message || 'Please log in to continue using the chat.',
        icon: error.icon || '🔐',
        retryable: false,
        showLogin: true,
        temporary: false
      });
      setIsStreaming(false);
    } else {
      // Use the error information directly from CompanionClient
      // The client already provides title, icon, and other metadata
      setError({
        type: error.type || 'error',
        title: error.title || 'Error',
        message: error.message,
        icon: error.icon || '⚠️',
        retryable: error.retryable !== false,
        retryAfter: error.retryAfter,
        showPolicyLink: error.showPolicyLink || error.showPolicy,
        showLogin: error.redirectToLogin || error.type === 'auth_failed',
        temporary: error.temporary
      });
      setIsStreaming(false);
    }
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || isStreaming || !clientRef.current || !token || !isTokenReady || !clientInitialized) {
      console.log('CompanionChat: Cannot send message - missing requirements:', {
        hasInput: !!input.trim(),
        isStreaming,
        hasClient: !!clientRef.current,
        hasToken: !!token,
        isTokenReady,
        clientInitialized
      });
      return;
    };

    const userMessage = input.trim();
    const messageId = crypto.randomUUID();
    setInput('');
    setError(null);

    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
      messageId,
      status: connectionState === 'offline' ? 'offline' : 'sending'
    };
    setMessages(prev => [...prev, userMsg]);

    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }

    setIsStreaming(true);
    setStreamingMessage('');
    streamingToolCalls.current = [];

    try {
      await clientRef.current.send(userMessage, messageId);
      
      setMessages(prev => 
        prev.map(msg => 
          msg.messageId === messageId 
            ? { ...msg, status: 'sent' }
            : msg
        )
      );
    } catch (error: any) {
      console.error('CompanionChat: Send message error:', error);
      
      // Handle auth errors by triggering reinitialization
      if ((error as any).type === 'auth_failed' || (error as any).type === 'auth_timeout') {
        console.log('CompanionChat: Auth error detected, may need reinitialization');
      }
      
      setMessages(prev => 
        prev.map(msg => 
          msg.messageId === messageId 
            ? { ...msg, status: 'error' }
            : msg
        )
      );
      
      // Use the error information directly from CompanionClient
      // The client already provides comprehensive error mapping
      const errorState: ErrorState = {
        type: (error as any).type || 'unknown_error',
        title: (error as any).title || 'Send Failed',
        message: error.message || 'Failed to send message. Please try again.',
        icon: (error as any).icon || '❌',
        retryable: (error as any).retryable !== false,
        retryAfter: (error as any).retryAfter,
        showLogin: (error as any).showLogin || (error as any).redirectToLogin || ((error as any).type === 'auth_failed'),
        status: (error as any).status
      };
      
      setError(errorState);
      setIsStreaming(false);
      
      // Implement automatic retry for transient errors
      if (errorState.retryable && shouldAutoRetry(errorState.type)) {
        const currentRetry = retryState?.messageId === messageId ? retryState : { messageId, attempts: 0, maxAttempts: 3 };
        
        if (currentRetry.attempts < currentRetry.maxAttempts) {
          // Use retryAfter from error if provided, otherwise exponential backoff
          const retryDelay = errorState.retryAfter 
            ? errorState.retryAfter * 1000 
            : Math.min(1000 * Math.pow(2, currentRetry.attempts), 10000); // Max 10s delay
          
          console.log(`CompanionChat: Auto-retrying in ${retryDelay}ms (attempt ${currentRetry.attempts + 1}/${currentRetry.maxAttempts})`);
          
          setRetryState({ ...currentRetry, attempts: currentRetry.attempts + 1 });
          
          setTimeout(async () => {
            try {
              setError({
                ...errorState,
                title: 'Retrying...',
                message: `Retrying message (attempt ${currentRetry.attempts + 1}/${currentRetry.maxAttempts})...`,
                icon: '🔄',
                temporary: true
              });
              
              setIsStreaming(true);
              // Find the original message to retry
              const originalMessage = messages.find(m => m.messageId === messageId);
              if (originalMessage && clientRef.current) {
                await clientRef.current.send(originalMessage.content, messageId);
                
                // Success - clear retry state and error
                setRetryState(null);
                setError(null);
              }
            } catch (retryError) {
              console.error('CompanionChat: Retry failed:', retryError);
              // Will trigger this error handler again
            }
          }, retryDelay);
        } else {
          setRetryState(null);
          console.log('CompanionChat: Max retry attempts exceeded');
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return;
      } else {
        e.preventDefault();
        sendMessage();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  useEffect(() => {
    if (!isStreaming && streamingMessage) {
      const assistantMsg: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: streamingMessage,
        timestamp: new Date(),
        toolCalls: streamingToolCalls.current,
        messageId: crypto.randomUUID(),
        status: 'sent'
      };
      setMessages(prev => [...prev, assistantMsg]);
      setStreamingMessage('');
      streamingToolCalls.current = [];
    }
  }, [isStreaming, streamingMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'end'
    });
  }, [messages, streamingMessage]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [isStreaming]);

  const handleOfflineMessage = (message: string, messageId: string) => {
    setOfflineMessageCount(prev => prev + 1);
  };

  const handleExportWarning = (warning: any) => {
    console.warn('Export warning:', warning);
  };

  const handleAccountDeleted = () => {
    window.location.href = '/login';
  };

  const handleTabMessage = (message: Message) => {
    // Sync messages from other tabs
    setMessages(prev => {
      // Check if message already exists
      const exists = prev.some(m => m.messageId === message.messageId);
      if (exists) return prev;
      
      // Add message and sort by timestamp
      return [...prev, message].sort((a, b) => 
        a.timestamp.getTime() - b.timestamp.getTime()
      );
    });
  };

  const handleExport = async () => {
    if (clientRef.current) {
      const data = await clientRef.current.export();
      console.log('Export data:', data);
    }
  };

  const handleDelete = async () => {
    if (clientRef.current && confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      await clientRef.current.delete();
    }
  };

  const loadCachedConversations = async () => {
    // First, load cached data immediately for fast display
    const cached = localStorage.getItem('companion_cache');
    if (cached) {
      try {
        const { messages: cachedMessages } = JSON.parse(cached);
        setMessages(cachedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        })));
      } catch (e) {
        console.error('Failed to load cached conversations:', e);
      }
    }

    // Then load from backend and merge (only if client is ready)
    if (clientRef.current && token && isTokenReady) {
      try {
        const backendHistory = await clientRef.current.getConversationHistory(50);
        
        setMessages(prevMessages => {
          // Create a map to avoid duplicates
          const messageMap = new Map();
          
          // Add backend messages first
          backendHistory.forEach(msg => {
            messageMap.set(msg.messageId, msg);
          });
          
          // Add cached messages that aren't in backend
          prevMessages.forEach(msg => {
            const msgId = msg.messageId || msg.id;
            if (!messageMap.has(msgId)) {
              messageMap.set(msgId, msg);
            }
          });
          
          // Convert to array and sort chronologically
          return Array.from(messageMap.values()).sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        });
        
        console.log('Loaded and merged conversation history');
      } catch (error) {
        console.error('Failed to load backend history:', error);
        // Continue with cached messages only - graceful degradation
      }
    }
  };

  const handleEmotionCheckIn = async (emotionData: any) => {
    try {
      setShowEmotionCheckIn(false);
      
      // Create a unique ID for this emotion check-in
      const messageId = crypto.randomUUID();
      
      // Add the emotion entry as a special message type
      const emotionMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: '', // Empty content since we'll use emotionData
        timestamp: new Date(),
        messageId,
        status: 'sending',
        emotionData: {
          ...emotionData,
          timestamp: emotionData.timestamp || new Date().toISOString()
        }
      };
      
      setMessages(prev => [...prev, emotionMessage]);
      setIsStreaming(true);
      
      // Send emotion check-in to backend
      if (clientRef.current && token && isTokenReady) {
        const response = await fetch(`${getApiBaseUrl()}/api/chat/emotion-checkin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(emotionData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to submit emotion check-in');
        }
        
        const result = await response.json();
        
        // Update message status
        setMessages(prev => 
          prev.map(msg => 
            msg.messageId === messageId 
              ? { ...msg, status: 'sent' }
              : msg
          )
        );
        
        // Add AI response
        const assistantMsg: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: result.response,
          timestamp: new Date(),
          messageId: crypto.randomUUID(),
          status: 'sent'
        };
        
        setMessages(prev => [...prev, assistantMsg]);
        announce('AI has responded to your emotion check-in');
      }
    } catch (error) {
      console.error('Emotion check-in error:', error);
      setError({
        type: 'error',
        title: 'Check-in Failed',
        message: 'Failed to submit emotion check-in. Please try again.',
        icon: '❌',
        retryable: true
      });
    } finally {
      setIsStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-base-900 text-base-100">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-secondary-600 text-white px-4 py-2 rounded-lg z-50">
        Skip to main content
      </a>
      
      <header className="flex items-center justify-between p-4 border-b border-base-800 bg-base-850/50 backdrop-blur">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">AI Companion</h1>
          <ConnectionIndicator state={connectionState} />
        </div>
        
        <div className="flex items-center gap-2">
          {offlineMessageCount > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 text-sm bg-accent-500/20 rounded-lg">
              <span>📤</span>
              <span>{offlineMessageCount} pending</span>
            </div>
          )}
          
          <button
            onClick={() => setShowExportModal(true)}
            className="px-3 py-1 text-sm transition bg-base-800 hover:bg-base-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Export conversation data"
          >
            Export
          </button>
          
          <ThemeToggle />
          
          <SettingsMenu onDelete={handleDelete} />
        </div>
      </header>

      <main 
        id="main-content"
        className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth"
        role="log"
        aria-live="polite"
        aria-label="Conversation history"
      >
        {authLoading || !isTokenReady || !clientInitialized ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-3 text-base-400">
              <Spinner />
              <span>
                {authLoading ? 'Authenticating...' : 
                 !isTokenReady ? 'Validating session...' :
                 'Initializing chat client...'}
              </span>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <WelcomeMessage />
        ) : null}

        {messages.map((msg) => (
          msg.emotionData ? (
            <EmotionEntryMessage 
              key={msg.id} 
              emotionData={msg.emotionData}
              textNote={msg.emotionData.textNote}
            />
          ) : (
            <MessageBubble key={msg.id} message={msg} />
          )
        ))}

        {isStreaming && streamingMessage && (
          <div className="flex gap-3" role="status" aria-live="assertive">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm bg-primary-500 text-white">
              AI
            </div>
            <div className="flex-1">
              <div className="p-3 max-w-2xl bg-base-800 rounded-lg">
                <ReactMarkdown 
                  className="prose prose-invert prose-sm max-w-none"
                  remarkPlugins={[remarkGfm]}
                >
                  {streamingMessage}
                </ReactMarkdown>
                <TypingIndicator />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </main>

      {error && (
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
      )}

      <footer className="p-4 border-t border-base-800 bg-base-850/30 backdrop-blur">
        <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowEmotionCheckIn(true)}
            disabled={authLoading || !isTokenReady || !clientInitialized}
            className="p-3 transition bg-base-800 hover:bg-base-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Open emotion check-in"
            title="Emotion check-in"
          >
            <SmilePlus className="w-5 h-5" />
          </button>
          
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={authLoading || !isTokenReady || !clientInitialized ? 
                'Initializing...' :
                connectionState === 'offline' ? 
                  'Type your message (will send when back online)...' :
                  'Type your message...'
              }
              disabled={isStreaming || authLoading || !isTokenReady || !clientInitialized}
              rows={1}
              className="w-full px-4 py-3 bg-base-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 resize-none overflow-hidden"
              style={{ minHeight: '52px' }}
              aria-label="Message input"
            />
            
            {input.length > 500 && (
              <div className="absolute bottom-1 right-1 text-xs px-1 text-base-400 bg-base-900 rounded">
                {input.length}/2000
              </div>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isStreaming || !input.trim() || input.length > 2000 || !clientInitialized || !isTokenReady || authLoading}
            className={`px-6 py-3 transition font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed ${
              isStreaming || !input.trim() || input.length > 2000 || !clientInitialized || !isTokenReady || authLoading
                ? 'bg-base-700 text-base-400'
                : 'bg-primary-500 text-white hover:bg-primary-600'
            }`}
            aria-label="Send message"
          >
            {isStreaming ? (
              <span className="flex items-center gap-2">
                <Spinner />
                <span className="hidden sm:inline">Sending</span>
              </span>
            ) : authLoading || !isTokenReady || !clientInitialized ? (
              <span className="flex items-center gap-2">
                <Spinner />
                <span className="hidden sm:inline">Loading</span>
              </span>
            ) : (
              <span>Send</span>
            )}
          </button>
        </form>
        
        <div className="flex justify-between items-center mt-2 text-xs text-base-500">
          <span>Press Shift+Enter for new line</span>
          <span>{connectionState === 'offline' ? '📱 Offline mode' : ''}</span>
        </div>
      </footer>

      {showExportModal && (
        <ExportModal 
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
        />
      )}
      
      {showEmotionCheckIn && (
        <EmotionCheckIn
          onClose={() => setShowEmotionCheckIn(false)}
          onComplete={handleEmotionCheckIn}
        />
      )}
    </div>
  );
}